import * as moment from 'moment';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { VbWithdrawalAmount } from '../../../../../models/shared/vb-withdrawal-amount';
import { MemberAccount } from '../../../../../models/transactions/member-account';
import { VariableBenefitConfigurationPaymentFormGroup } from '../form-groups/variable-benefit-configuration-payment-form-group';
import { VariableBenefitConfigurationJurisdictionPaymentFormGroup } from '../form-groups/variable-benefit-configuration-jurisdiction-payment-form-group';
import { GOVERNING_JURISDICTION_NAMES } from '../../../../../consts/consts';
import { GoverningJurisdictionTypeId } from '../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { Alert } from '../../../../../models/alerts/dto/alert';
import { AlertBackgroundColorEnum } from '../../../../../models/alerts/enum/alert-background-color.enum';
import { VariableBenefitTargetPayment } from '../../../../../models/submissions/variable-benefit-target-payment';
import { DateUtils } from '../../../../../utils/date-utils';
import { VbTarget } from '../../../../../models/transactions/vb-target';
import { RecurringPaymentAmountTypeEnum } from '../enums/recurring-payment-amount-type.enum';
import { INTERNAL_PAYMENT_CUTOFF_DAY } from './variable-benefit-payment-date-utils';

export interface JurisdictionPaymentCardData {
  form: VariableBenefitConfigurationJurisdictionPaymentFormGroup;
  governingJurisdictionId: number | null;
  jurisdictionName: string;
  estimatedBalance: number;
  minAnnualAmount: number;
  maxAnnualAmount: number | null;
  maximumRaised: boolean;
  isNew: boolean;
}

export function getVisibleJurisdictionFormGroups(
  stepForm: VariableBenefitConfigurationPaymentFormGroup
): VariableBenefitConfigurationJurisdictionPaymentFormGroup[] {
  return stepForm.controls.jurisdictionDetails.controls.filter(fg => fg.controls.accountBalance.value > 0);
}

export function buildJurisdictionPaymentCards(
  paymentForm: VariableBenefitConfigurationPaymentFormGroup,
  minMaxResults: VbWithdrawalAmount[] | null,
  vbAccounts?: MemberAccount[]
): JurisdictionPaymentCardData[] {
  const existingJurisdictionIds = vbAccounts ? new Set(vbAccounts.map(a => a.governingJurisdictionId)) : null;

  return getVisibleJurisdictionFormGroups(paymentForm)
    .map(fg => {
      const jurisdictionId = fg.controls.governingJurisdictionId.value;
      const minMax = minMaxResults?.find(r => r.governingJurisdictionId === jurisdictionId) ?? null;
      const jurisdictionName =
        jurisdictionId === null
          ? 'Non-Locked-In'
          : GOVERNING_JURISDICTION_NAMES[jurisdictionId as GoverningJurisdictionTypeId] ?? '';
      return {
        form: fg,
        governingJurisdictionId: jurisdictionId,
        jurisdictionName,
        estimatedBalance: fg.controls.accountBalance.value,
        minAnnualAmount: minMax?.minAnnualWithdrawalAmount ?? 0,
        maxAnnualAmount: minMax?.maxAnnualWithdrawalAmount ?? 0,
        // The note explains a maximum equal to the minimum. A non-locked-in account has no maximum to raise, and new
        // money in a raised account lifts its maximum back above the minimum
        maximumRaised:
          jurisdictionId !== null &&
          !!minMax &&
          (minMax.maximumRaisedToMinimumAmount ?? 0) > 0 &&
          minMax.maxAnnualWithdrawalAmount <= minMax.minAnnualWithdrawalAmount,
        isNew: existingJurisdictionIds !== null ? !existingJurisdictionIds.has(jurisdictionId) : false
      };
    })
    .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
}

export function buildScheduledChangeCutoffBanner(): Alert {
  const currentMonth = moment().format('MMMM');

  const alert = new Alert();
  alert.title = $localize`The ${currentMonth} Cut-Off Date has Passed`;
  alert.description = $localize`The cut-off date for adjusting ${currentMonth}'s recurring payments has passed.`;
  alert.colourId = AlertBackgroundColorEnum.BannerYellow;
  alert.skipTitleTruncation = true;
  alert.skipDescriptionTruncation = true;
  alert.dismissable = false;
  alert.colour.backgroundColour = '#fcf6eb';
  alert.colour.borderColour = '#d2811c';
  return alert;
}

// A share of zero, or an account the split leaves out, empties the row: the submit counts a missing amount as $0
export function patchJurisdictionPaymentAmounts(
  form: VariableBenefitConfigurationPaymentFormGroup,
  split: VariableBenefitTargetPayment
): void {
  getVisibleJurisdictionFormGroups(form).forEach(fg => {
    const jurisdictionId = fg.controls.governingJurisdictionId.value;
    const share = split.accounts.find(a => a.governingJurisdictionId === jurisdictionId)?.recurringPaymentAmount;
    fg.controls.recurringPaymentAmount.setValue(share ? share : null);
  });
}

export function buildYellowBanner(title: string, description: string, dismissable: boolean): Alert {
  const alert = new Alert();
  alert.title = title;
  alert.description = description;
  alert.colourId = AlertBackgroundColorEnum.BannerYellow;
  alert.skipTitleTruncation = true;
  alert.skipDescriptionTruncation = true;
  alert.dismissable = dismissable;
  alert.colour.backgroundColour = '#fcf6eb';
  alert.colour.borderColour = '#d2811c';
  return alert;
}

export function buildCutOffBanner(): Alert {
  const currentMonth = moment().format('MMMM');

  return buildYellowBanner(
    $localize`The ${currentMonth} Cut-Off Date has Passed`,
    $localize`The cut-off date for adjusting the upcoming payment has passed. Additional action may be required to ensure this instruction is captured in the next payment run.`,
    true
  );
}

export function showCutOffBanner$(form: VariableBenefitConfigurationPaymentFormGroup): Observable<boolean> {
  const { recurringPaymentStartMonth, recurringPaymentStartYear } = form.controls;
  return combineLatest([
    recurringPaymentStartMonth.valueChanges.pipe(startWith(recurringPaymentStartMonth.value)),
    recurringPaymentStartYear.valueChanges.pipe(startWith(recurringPaymentStartYear.value)),
    recurringPaymentStartMonth.statusChanges.pipe(startWith(recurringPaymentStartMonth.status))
  ]).pipe(
    map(([month, year]) => {
      const today = moment(DateUtils.todayInCST());
      const cutoffPassed = today.date() > INTERNAL_PAYMENT_CUTOFF_DAY;
      const hasEffectiveDateError = !!recurringPaymentStartMonth.errors?.recurringPaymentStartBeforeEffectiveDate;
      return year === today.year() && month === today.month() + 1 && cutoffPassed && !hasEffectiveDateError;
    })
  );
}

// A target payment is split across two or more accounts, so it can't survive one of them being depleted.
export function isTargetPaymentNoLongerValid(target: VbTarget | null, fundedAccountCount: number): boolean {
  return (target?.targetRecurringPaymentAmount ?? null) !== null && fundedAccountCount === 1;
}

// Emits on purpose: once VariableBenefitConfigurationPaymentFormStateService is watching the form, it restores
// the amounts it saved on entering target mode when custom mode comes back, so it has to see the switch before
// the amounts are cleared.
export function clearPaymentsIfTargetPaymentNoLongerValid(form: VariableBenefitConfigurationPaymentFormGroup): void {
  const jurisdictionForms = getVisibleJurisdictionFormGroups(form);
  if (form.controls.useCustomPayments.value || jurisdictionForms.length !== 1) return;

  form.patchValue({
    useCustomPayments: true,
    targetRecurringPaymentAmount: null,
    lumpSumPaymentDesiredPaymentDate: null
  });
  jurisdictionForms.forEach(fg => {
    fg.patchValue({
      recurringPaymentType: RecurringPaymentAmountTypeEnum.Custom,
      recurringPaymentAmount: null,
      hasLumpSumPayment: false,
      lumpSumPaymentAmount: null
    });
    fg.controls.lumpSumPaymentAmount.disable();
  });
}

export function buildTargetPaymentNoLongerValidBanner(): Alert {
  const alert = new Alert();
  alert.title = $localize`The Recurring Payments must be Reconfigured`;
  alert.description = $localize`The target payment for the Variable Benefits accounts is no longer valid due to one or more of the accounts being depleted. Reconfigure the recurring payments for the remaining account.`;
  alert.colourId = AlertBackgroundColorEnum.BannerYellow;
  alert.skipTitleTruncation = true;
  alert.skipDescriptionTruncation = true;
  alert.dismissable = true;
  alert.colour.backgroundColour = '#fcf6eb';
  alert.colour.borderColour = '#d2811c';
  return alert;
}

export function buildPaymentChangeEstimateBanner(): Alert {
  return buildEstimateBanner(
    $localize`Annual Min, Annual Max and Est. Annual Total for recurring and lump sum payments will be estimated on the previous calendar year as a result of your date selection.`
  );
}

export function buildUpdateConfigurationEstimateBanner(): Alert {
  return buildEstimateBanner(
    $localize`Annual Min, Annual Max and Est. Annual Total for recurring and lump sum payments will be estimated on the previous calendar year as a result of the effective date chosen in Step 2.`
  );
}

function buildEstimateBanner(description: string): Alert {
  const alert = new Alert();
  alert.title = $localize`Estimates will be Based on the Previous Year`;
  alert.description = description;
  alert.colourId = AlertBackgroundColorEnum.BannerBlue;
  alert.skipTitleTruncation = true;
  alert.skipDescriptionTruncation = true;
  alert.dismissable = true;
  alert.colour.backgroundColour = '#e6eef1';
  alert.colour.borderColour = '#03536b';
  return alert;
}
